import {createReviewer, approveReviewer, deleteReviewer, createAccount} from 'core/api/study_reviewer'
import {startSubmit, stopSubmit, setSubmitSucceeded} from 'redux-form'
import {all, call, put, fork, takeLatest} from 'redux-saga/effects'
import * as routes from 'study_reviewer/routes'
import {IActionRegisterRequest, IActionApproveRequest, IActionPasswordRequest, IActionDeleteRequest, IActionCreateAccountRequest, setHasCreatedAccount} from './actions'
import {REGISTER_FORM_NAME, APPROVE_FORM_NAME, PASSWORD_FORM_NAME, DELETE_REQUEST_FORM_NAME, CREATE_ACCOUNT_FORM_NAME} from './actionTypes'
import {historyPushRequest} from '../history'
import { getTime } from 'date-fns'

/**
 * Dependencies that need to be injected into the main saga
 */
export interface IMainSagaDependencies {
  withAuth: (fn: (...args: any[]) => any) => void
}

/**
 * Dependencies needed for this saga
 */
export interface IDependencies extends IMainSagaDependencies {
}

export function* registerRequest(action: IActionRegisterRequest) {
  yield put(startSubmit(action.form))
  delete action.payload.confirmNpi
  try {
    const response = yield call(createReviewer, action.payload)
    if (response.status === 200) {
      yield put(stopSubmit(action.form))
      yield put(historyPushRequest("/register-confirm"))
    }
    if (response.status === 409) {
      const error = response.data.message
      yield put(stopSubmit(action.form, {_error: error}))
    }
  } catch (e) {
    if (!e.response) {
      // TODO I18N
      const error = 'Network error. Please check your connection and try again.'
      yield put(stopSubmit(action.form, {_error: error}))
    } else {
      // TODO I18N
      let error = 'Server-side error'
      if (e.response && e.response.status === 409) {
        // TODO I18N
        error = 'Duplicate Error'
      }
      yield put(stopSubmit(action.form, {_error: error}))
    }
  }
}

export function* approveRequest(dependencies: IDependencies, action: IActionApproveRequest) {
  yield put(startSubmit(action.form))
  const id = action.payload.id;
  const date = new Date();
  const isoDate = date.toISOString();
  action.payload.isApproved = true
  action.payload.approvedTime = isoDate.toString()
  delete action.payload.id
  delete action.payload.confirmPassword
  try {
    const response = yield call(dependencies.withAuth, approveReviewer, id, action.payload)
    if (response.status === 200) {
      yield put(stopSubmit(action.form))
      yield put(historyPushRequest("/physicians"))
    }
    if (response.status === 409) {
      const error = response.data.message
      yield put(stopSubmit(action.form, { _error: error }))
    }
  } catch (e) {
    if (!e.response) {
      // TODO I18N
      const error = 'Network error. Please check your connection and try again.'
      yield put(stopSubmit(action.form, { _error: error }))
    } else {
      // TODO I18N
      let error = 'Server-side error'
      if (e.response && e.response.status === 409) {
        // TODO I18N
        error = 'Duplicate Error'
      }
      yield put(stopSubmit(action.form, { _error: error }))
    }
  }
}

export function* deleteRequest(dependencies: IDependencies, action: IActionDeleteRequest) {
  yield put(startSubmit(action.form))
  const id = action.payload.id;
  try {
    const response = yield call(dependencies.withAuth, deleteReviewer, id)
    if (response.status === 200) {
      yield put(stopSubmit(action.form))
      yield put(historyPushRequest("/physicians"))
    }
    if (response.status === 409) {
      const error = response.data.message
      yield put(stopSubmit(action.form, { _error: error }))
    }
  } catch (e) {
    if (!e.response) {
      // TODO I18N
      const error = 'Network error. Please check your connection and try again.'
      yield put(stopSubmit(action.form, { _error: error }))
    } else {
      // TODO I18N
      let error = 'Server-side error'
      if (e.response && e.response.status === 409) {
        // TODO I18N
        error = 'Duplicate Error'
      }
      yield put(stopSubmit(action.form, { _error: error }))
    }
  }
}

export function* changePasswordRequest(dependencies: IDependencies, action: IActionPasswordRequest) {
  yield put(startSubmit(action.form))
  const id = action.payload.id;
  delete action.payload.isApproved
  delete action.payload.approvedTime
  delete action.payload.id
  delete action.payload.confirmPassword
  try {
    const response = yield call(dependencies.withAuth, approveReviewer, id, action.payload)
    if (response.status === 200) {
      yield put(stopSubmit(action.form))
      yield put(historyPushRequest("/logout"))
    }
    if (response.status === 409) {
      const error = response.data.message
      yield put(stopSubmit(action.form, { _error: error }))
    }
  } catch (e) {
    if (!e.response) {
      // TODO I18N
      const error = 'Network error. Please check your connection and try again.'
      yield put(stopSubmit(action.form, { _error: error }))
    } else {
      // TODO I18N
      let error = 'Server-side error'
      if (e.response && e.response.status === 409) {
        // TODO I18N
        error = 'Duplicate Error'
      }
      yield put(stopSubmit(action.form, { _error: error }))
    }
  }
}

export function* createAccountRequest(action: IActionCreateAccountRequest) {
  yield put(startSubmit(action.form))
  try {
    const response = yield call(createAccount, action.payload)
    if (response.status === 200) {
      yield put(stopSubmit(action.form))
      yield put(setHasCreatedAccount(true))
    }
    if (response.status === 409) {
      const error = response.data.message
      yield put(stopSubmit(action.form, { _error: error }))
    }
  } catch (e) {
    if (!e.response) {
      // TODO I18N
      const error = 'Network error. Please check your connection and try again.'
      yield put(stopSubmit(action.form, { _error: error }))
    } else {
      // TODO I18N
      let error = 'Server-side error'
      if (e.response && e.response.status === 409) {
        // TODO I18N
        error = 'Duplicate Error'
      }
      yield put(stopSubmit(action.form, { _error: error }))
    }
  }
}

function* mainSaga(dependencies: IMainSagaDependencies) {
  yield all([
    takeLatest(
      REGISTER_FORM_NAME,
      registerRequest,
    ),
    takeLatest<IActionApproveRequest, IDependencies>(
      APPROVE_FORM_NAME,
      approveRequest,
      dependencies,
    ),
    takeLatest<IActionPasswordRequest, IDependencies>(
      PASSWORD_FORM_NAME,
      changePasswordRequest,
      dependencies
    ),
    takeLatest(CREATE_ACCOUNT_FORM_NAME, createAccountRequest),
    takeLatest(DELETE_REQUEST_FORM_NAME, deleteRequest, dependencies)
  ])
}

function* saga(dependencies: IMainSagaDependencies) {
  yield all([
    fork<IMainSagaDependencies>(mainSaga, dependencies),
  ])
}

export default saga



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./study_reviewer/src/store/register/saga.ts